<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format, parseISO, isValid } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import { useDocumentsStore } from 'dashboard/stores/documents';
import {
  DOCUMENT_STATUS_CLASSES,
  DOCUMENT_TYPE_KEYS,
  documentStatusLabel,
} from 'dashboard/components-next/CRM/Documents/constants';
import RecordHeader from 'dashboard/components-next/CRM/RecordWorkspace/RecordHeader.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RequestDocumentDialog from 'dashboard/components-next/CRM/Documents/RequestDocumentDialog/RequestDocumentDialog.vue';
import SendForSignatureDialog from 'dashboard/components-next/CRM/Documents/SendForSignatureDialog/SendForSignatureDialog.vue';
import DocusealSigningDialog from 'dashboard/components-next/CRM/Documents/DocusealSigningDialog/DocusealSigningDialog.vue';
import DocusealBuilderDialog from 'dashboard/components-next/CRM/Documents/DocusealBuilderDialog/DocusealBuilderDialog.vue';

const route = useRoute();
const router = useRouter();
const store = useDocumentsStore();
const globalStore = useStore();
const { t, te } = useI18n();
const { isAdmin } = useAdmin();
const currentUser = useMapGetter('getCurrentUser');

const hasError = ref(false);
const selectedStatus = ref('');
const versionFileInput = ref(null);
const archiveDialogRef = ref(null);
const deleteDialogRef = ref(null);
const legalHoldDialogRef = ref(null);
const requestDialogRef = ref(null);
const signatureDialogRef = ref(null);
const signingDialogRef = ref(null);
const builderDialogRef = ref(null);
const voidDialogRef = ref(null);
const pendingVoidRequest = ref(null);
const isSavingAccess = ref(false);
const selectedVisibility = ref('');
const newGrantType = ref('User');
const newGranteeId = ref('');
// Upload links are returned only in the create response, so keep the URLs
// of requests created in this session for the copy-link button.
const sessionLinks = ref({});

const documentId = computed(() => parseInt(route.params.documentId, 10));
const document = computed(() => store.getSelectedDocument);
const uiFlags = computed(() => store.getUIFlags);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);
const isDeleting = computed(() => uiFlags.value.deleting);
const isTransitioning = computed(() => store.isTransitioning(documentId.value));
const isUploading = computed(() => store.isUploading(documentId.value));
const isDownloading = computed(() => store.isDownloading(documentId.value));

const breadcrumbItems = computed(() => [
  { label: t('DOCUMENTS.TITLE'), link: '#' },
  ...(document.value ? [{ label: document.value.title }] : []),
]);

const statusLabel = status => (status ? documentStatusLabel(status, t, te) : '');

const currentStatusLabel = computed(() => statusLabel(document.value?.status));
const currentStatusClass = computed(
  () => DOCUMENT_STATUS_CLASSES[document.value?.status] || DOCUMENT_STATUS_CLASSES.draft
);

const typeLabel = computed(() => {
  const type = document.value?.documentType;
  if (!type) return '-';
  return DOCUMENT_TYPE_KEYS.includes(type) ? t(`DOCUMENTS.TYPES.${type.toUpperCase()}`) : type;
});

// The show payload gains a full versions list; fall back to the current
// version so the section still works with the index-shaped payload.
const versions = computed(() => {
  if (document.value?.versions?.length) return document.value.versions;
  return document.value?.currentVersion ? [document.value.currentVersion] : [];
});

const history = computed(() => document.value?.history || []);
const nextStatuses = computed(() => document.value?.nextStatuses || []);
const linkedRecords = computed(() => document.value?.links || []);

// State events and access events share one timeline, newest first. The `type`
// discriminator keeps the markers and labels apart in the template.
const timelineEvents = computed(() => {
  const stateEvents = history.value.map(event => ({ ...event, type: 'state' }));
  const accessEvents = (document.value?.accessEvents || []).map(event => ({
    ...event,
    type: 'access',
  }));
  return [...stateEvents, ...accessEvents].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
});

const nextStatusOptions = computed(() =>
  nextStatuses.value.map(status => ({
    value: status,
    label: statusLabel(status),
  }))
);

const canDelete = computed(
  () => isAdmin.value && ['draft', 'requested', 'archived'].includes(document.value?.status)
);

// Access management mirrors the backend guard: administrators plus the
// document's creator/owner may change visibility and grants.
const canManageAccess = computed(
  () =>
    isAdmin.value ||
    currentUser.value?.id === document.value?.creator?.id ||
    currentUser.value?.id === document.value?.owner?.id
);

const grants = computed(() => document.value?.grants || []);

const requests = computed(() => store.getDocumentRequests);
const isCreatingRequest = computed(() => uiFlags.value.creatingRequest);

const conversationMessages = computed(() => store.conversationMessages || []);

const signatureRequests = computed(() => document.value?.signatureRequests || []);
const isCreatingSignatureRequest = computed(() => uiFlags.value.creatingSignatureRequest);
const canSendForSignature = computed(() => nextStatuses.value.includes('awaiting_signature'));
const hasDocumentFile = computed(() => !!document.value?.currentVersion);

// Canonical chip recipe per request state: pending/sent/viewed amber,
// completed teal, declined ruby, voided/expired slate.
const SIGNATURE_STATUS_CLASSES = {
  pending: 'bg-n-amber-9/10 text-n-amber-11',
  sent: 'bg-n-amber-9/10 text-n-amber-11',
  viewed: 'bg-n-amber-9/10 text-n-amber-11',
  completed: 'bg-n-teal-9/10 text-n-teal-11',
  declined: 'bg-n-ruby-9/10 text-n-ruby-11',
  voided: 'bg-n-slate-9/10 text-n-slate-11',
  expired: 'bg-n-slate-9/10 text-n-slate-11',
};
const TERMINAL_SIGNATURE_STATUSES = ['completed', 'declined', 'voided', 'expired'];

const isTerminalSignature = request => TERMINAL_SIGNATURE_STATUSES.includes(request.status);

const signatureStatusLabel = status => {
  const labels = {
    pending: t('DOCUMENTS.SIGNATURES.STATE.PENDING'),
    sent: t('DOCUMENTS.SIGNATURES.STATE.SENT'),
    viewed: t('DOCUMENTS.SIGNATURES.STATE.VIEWED'),
    completed: t('DOCUMENTS.SIGNATURES.STATE.COMPLETED'),
    declined: t('DOCUMENTS.SIGNATURES.STATE.DECLINED'),
    voided: t('DOCUMENTS.SIGNATURES.STATE.VOIDED'),
    expired: t('DOCUMENTS.SIGNATURES.STATE.EXPIRED'),
  };
  return labels[status] || status;
};

const signatureStatusClass = status =>
  SIGNATURE_STATUS_CLASSES[status] || SIGNATURE_STATUS_CLASSES.pending;

const providerLabel = provider => {
  if (!provider) return '';
  const labels = {
    sandbox: t('DOCUMENTS.SIGNATURES.DIALOG.PROVIDER_SANDBOX'),
    docuseal: t('DOCUMENTS.SIGNATURES.DIALOG.PROVIDER_RUUT_SIGN'),
  };
  return labels[provider] || provider;
};

// Canonical chip recipe per request state: Active amber, Expired slate,
// Revoked ruby, Completed teal.
const REQUEST_STATE_CLASSES = {
  active: 'bg-n-amber-9/10 text-n-amber-11',
  expired: 'bg-n-slate-9/10 text-n-slate-11',
  revoked: 'bg-n-ruby-9/10 text-n-ruby-11',
  completed: 'bg-n-teal-9/10 text-n-teal-11',
};

const requestState = request => {
  if (request.uploadedAt) return 'completed';
  if (request.revokedAt) return 'revoked';
  if (request.expiresAt) {
    const expiry = parseISO(request.expiresAt);
    if (isValid(expiry) && expiry <= new Date()) return 'expired';
  }
  return 'active';
};

const requestStateLabel = request => {
  const labels = {
    active: t('DOCUMENTS.REQUESTS.STATE.ACTIVE'),
    expired: t('DOCUMENTS.REQUESTS.STATE.EXPIRED'),
    revoked: t('DOCUMENTS.REQUESTS.STATE.REVOKED'),
    completed: t('DOCUMENTS.REQUESTS.STATE.COMPLETED'),
  };
  return labels[requestState(request)];
};

const requestStateClass = request => REQUEST_STATE_CLASSES[requestState(request)];

const canRevokeRequest = request => isAdmin.value || currentUser.value?.id === request.createdById;

const isRestricted = computed(() => document.value?.visibility === 'restricted');

const visibilityLabel = computed(() =>
  isRestricted.value
    ? t('DOCUMENTS.ACCESS.VISIBILITY_RESTRICTED')
    : t('DOCUMENTS.ACCESS.VISIBILITY_ACCOUNT')
);

const visibilityDescription = computed(() =>
  isRestricted.value
    ? t('DOCUMENTS.ACCESS.DESCRIPTION_RESTRICTED')
    : t('DOCUMENTS.ACCESS.DESCRIPTION_ACCOUNT')
);

const visibilityChipClass = computed(() =>
  isRestricted.value ? 'bg-n-ruby-9/10 text-n-ruby-11' : 'bg-n-slate-9/10 text-n-slate-11'
);

const isQuarantined = computed(() => document.value?.status === 'quarantined');

// Legal hold is admin-only on the backend; the toggle and confirm dialog
// copy flip with the current state.
const legalHoldDialogCopy = computed(() =>
  document.value?.legalHold
    ? {
        title: t('DOCUMENTS.ACCESS.LEGAL_HOLD_CONFIRM_DISABLE_TITLE'),
        description: t('DOCUMENTS.ACCESS.LEGAL_HOLD_CONFIRM_DISABLE_DESCRIPTION'),
      }
    : {
        title: t('DOCUMENTS.ACCESS.LEGAL_HOLD_CONFIRM_ENABLE_TITLE'),
        description: t('DOCUMENTS.ACCESS.LEGAL_HOLD_CONFIRM_ENABLE_DESCRIPTION'),
      }
);

const visibilityOptions = computed(() => [
  { value: 'account', label: t('DOCUMENTS.ACCESS.VISIBILITY_ACCOUNT') },
  { value: 'restricted', label: t('DOCUMENTS.ACCESS.VISIBILITY_RESTRICTED') },
]);

const grantTypeOptions = computed(() => [
  { value: 'User', label: t('DOCUMENTS.ACCESS.TYPE_USER') },
  { value: 'Team', label: t('DOCUMENTS.ACCESS.TYPE_TEAM') },
]);

// Account agents and teams come from the shared Vuex modules, the same
// source the CRM create dialogs use for their assignee selectors.
const granteeOptions = computed(() => {
  if (newGrantType.value === 'Team') {
    return (globalStore.getters['teams/getTeams'] || []).map(team => ({
      value: team.id,
      label: team.name,
    }));
  }
  return (globalStore.getters['agents/getAgents'] || []).map(agent => ({
    value: agent.id,
    label: agent.name || agent.email,
  }));
});

const granteePlaceholder = computed(() =>
  newGrantType.value === 'Team'
    ? t('DOCUMENTS.ACCESS.TEAM_PLACEHOLDER')
    : t('DOCUMENTS.ACCESS.USER_PLACEHOLDER')
);

const granteeTypeLabel = type =>
  type === 'Team' ? t('DOCUMENTS.ACCESS.TYPE_TEAM') : t('DOCUMENTS.ACCESS.TYPE_USER');

watch(
  document,
  doc => {
    selectedVisibility.value = doc?.visibility || 'account';
  },
  { immediate: true }
);

watch(newGrantType, () => {
  newGranteeId.value = '';
});

const formatDate = value => {
  if (!value) return '';
  try {
    const date = parseISO(value);
    if (!isValid(date)) return '';
    return format(date, 'MMM d, yyyy');
  } catch {
    return '';
  }
};

const dueDateLabel = computed(
  () => formatDate(document.value?.dueDate) || t('DOCUMENTS.NO_DUE_DATE')
);

watch(nextStatuses, statuses => {
  if (!statuses.includes(selectedStatus.value)) {
    selectedStatus.value = statuses[0] || '';
  }
});

const versionNumberLabel = version => `v${version.versionNumber}`;

const versionMeta = version => {
  const date = formatDate(version.createdAt);
  return version.uploadedBy?.name
    ? `${date} · ${t('DOCUMENTS.DETAIL.UPLOADED_BY', { name: version.uploadedBy.name })}`
    : date;
};

const transitionLabel = event =>
  event.fromStatus
    ? `${statusLabel(event.fromStatus)} → ${statusLabel(event.toStatus)}`
    : statusLabel(event.toStatus);

const accessEventLabel = event => {
  const version = versions.value.find(v => v.id === event.documentVersionId);
  return version
    ? t('DOCUMENTS.DETAIL.DOWNLOADED_VERSION', { number: version.versionNumber })
    : t('DOCUMENTS.DETAIL.DOWNLOADED');
};

const historyMeta = event =>
  `${event.actor?.name || t('DOCUMENTS.DETAIL.SYSTEM')} · ${formatDate(event.createdAt)}`;

const fetchDocument = async () => {
  if (!documentId.value) return;
  hasError.value = false;
  const result = await store.show(documentId.value);
  if (!result) hasError.value = true;
};

const goToDocumentsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push({
      name: 'crm_documents_index',
      params: { accountId: route.params.accountId },
    });
  }
};

const runTransition = async status => {
  try {
    await store.transition(documentId.value, status);
    useAlert(t('DOCUMENTS.TRANSITION_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.TRANSITION_ERROR'));
  }
};

const handleMove = () => {
  if (!selectedStatus.value) return;
  if (selectedStatus.value === 'archived') {
    archiveDialogRef.value?.open();
    return;
  }
  runTransition(selectedStatus.value);
};

const chooseVersionFile = () => {
  if (versionFileInput.value) {
    versionFileInput.value.value = '';
    versionFileInput.value.click();
  }
};

const uploadVersion = async event => {
  const [file] = event.target.files || [];
  if (!file) return;

  const payload = new FormData();
  payload.append('file', file);
  try {
    await store.createVersion(documentId.value, payload);
    useAlert(t('DOCUMENTS.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.UPLOAD_ERROR'));
  }
};

const handleDownload = async (versionId = null, filename = null) => {
  try {
    await store.download(documentId.value, {
      versionId,
      filename: filename || document.value?.currentVersion?.filename || document.value?.title,
    });
  } catch (error) {
    useAlert(t('DOCUMENTS.DOWNLOAD_ERROR'));
  }
};

const handleDelete = async () => {
  try {
    await store.delete(documentId.value);
    useAlert(t('DOCUMENTS.DETAIL.DELETE_SUCCESS'));
    router.push({
      name: 'crm_documents_index',
      params: { accountId: route.params.accountId },
    });
  } catch (error) {
    useAlert(t('DOCUMENTS.DETAIL.DELETE_ERROR'));
  }
};

const submitAccess = async payload => {
  isSavingAccess.value = true;
  try {
    await store.update(documentId.value, payload);
    useAlert(t('DOCUMENTS.ACCESS.UPDATE_SUCCESS'));
    return true;
  } catch (error) {
    useAlert(t('DOCUMENTS.ACCESS.UPDATE_ERROR'));
    return false;
  } finally {
    isSavingAccess.value = false;
  }
};

const changeVisibility = async value => {
  if (!value || value === document.value?.visibility) return;
  const changed = await submitAccess({ visibility: value });
  if (!changed) selectedVisibility.value = document.value?.visibility || 'account';
};

const addGrant = async () => {
  if (!newGranteeId.value) return;
  const added = await submitAccess({
    grants_attributes: [{ grantee_type: newGrantType.value, grantee_id: newGranteeId.value }],
  });
  if (added) newGranteeId.value = '';
};

const removeGrant = grant => {
  submitAccess({ grants_attributes: [{ id: grant.id, _destroy: '1' }] });
};

const toggleLegalHold = () => {
  submitAccess({ legal_hold: !document.value?.legalHold });
};

const fetchRequests = () => {
  if (documentId.value) store.fetchRequests(documentId.value);
};

const fetchConversationMessages = () => {
  if (documentId.value) store.fetchConversationMessages(documentId.value);
};

const openRequestDialog = () => requestDialogRef.value?.open();

const createRequest = async form => {
  try {
    const created = await store.createRequest({
      document_id: documentId.value,
      recipient_contact_id: form.contactId,
      contact_inbox_id: form.contactInboxId || null,
      message: form.message,
      expires_in_days: form.expiresInDays,
    });
    if (created?.url) {
      sessionLinks.value = { ...sessionLinks.value, [created.id]: created.url };
      requestDialogRef.value?.showLink(created.url, created.dispatch);
    }
  } catch (error) {
    useAlert(error?.message || t('DOCUMENTS.REQUESTS.CREATE_ERROR'));
  }
};

const copySessionLink = async id => {
  const url = sessionLinks.value[id];
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    useAlert(t('DOCUMENTS.REQUESTS.COPY_SUCCESS'));
  } catch {
    useAlert(t('DOCUMENTS.REQUESTS.COPY_ERROR'));
  }
};

const revokeRequest = async request => {
  try {
    await store.revokeRequest(request.id);
    useAlert(t('DOCUMENTS.REQUESTS.REVOKE_SUCCESS'));
  } catch (error) {
    useAlert(t('DOCUMENTS.REQUESTS.REVOKE_ERROR'));
  }
};

const signerMeta = request => {
  const parts = [];
  if (request.signerName && request.signerEmail) parts.push(request.signerEmail);
  parts.push(
    request.completedAt
      ? t('DOCUMENTS.SIGNATURES.COMPLETED_AT', { date: formatDate(request.completedAt) })
      : formatDate(request.createdAt)
  );
  parts.push(providerLabel(request.provider));
  return parts.filter(Boolean).join(' · ');
};

const openSignatureDialog = () => signatureDialogRef.value?.open();

const createSignatureRequest = async form => {
  try {
    await store.createSignatureRequest({
      document_id: documentId.value,
      signer_contact_id: form.contactId,
      contact_inbox_id: form.contactInboxId || null,
      message: form.message,
    });
    signatureDialogRef.value?.close();
    useAlert(t('DOCUMENTS.SIGNATURES.CREATE_SUCCESS'));
    fetchDocument();
  } catch (error) {
    useAlert(error?.message || t('DOCUMENTS.SIGNATURES.CREATE_ERROR'));
  }
};

const confirmVoidSignatureRequest = request => {
  pendingVoidRequest.value = request;
  voidDialogRef.value?.open();
};

const voidSignatureRequest = async () => {
  const request = pendingVoidRequest.value;
  pendingVoidRequest.value = null;
  if (!request) return;
  try {
    await store.voidSignatureRequest(request.id);
    useAlert(t('DOCUMENTS.SIGNATURES.VOID_SUCCESS'));
    fetchDocument();
  } catch (error) {
    useAlert(t('DOCUMENTS.SIGNATURES.VOID_ERROR'));
  }
};

const openSigningDialog = request => {
  if (!request.signingUrl) return;
  signingDialogRef.value?.open(request.signingUrl);
};

const handleSigningComplete = () => {
  useAlert(t('DOCUMENTS.SIGNATURES.SIGN_NOW_COMPLETED'));
  fetchDocument();
};

const openBuilderDialog = () => builderDialogRef.value?.open();

const handleBuilderSaved = async templateId => {
  try {
    await store.update(documentId.value, { docuseal_template_id: templateId });
    builderDialogRef.value?.close();
    useAlert(t('DOCUMENTS.SIGNATURES.BUILDER.SUCCESS'));
    fetchDocument();
  } catch (error) {
    useAlert(error?.message || t('DOCUMENTS.SIGNATURES.BUILDER.ERROR_LOAD'));
  }
};

onMounted(() => {
  fetchDocument();
  fetchRequests();
  fetchConversationMessages();
  globalStore.dispatch('agents/get');
  globalStore.dispatch('teams/get');
});
</script>

<template>
  <div class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background">
    <!-- Loading State -->
    <div v-if="isFetchingItem" class="flex items-center justify-center flex-1 h-full">
      <Spinner />
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="flex flex-col items-center justify-center flex-1 h-full gap-4">
      <span class="i-lucide-alert-circle text-n-ruby-9 size-12" />
      <p class="text-n-slate-11">{{ t('DOCUMENTS.DETAIL.FETCH_ERROR') }}</p>
      <Button :label="t('DOCUMENTS.DETAIL.RETRY')" @click="fetchDocument" />
    </div>

    <!-- Content -->
    <section
      v-else-if="document"
      class="flex flex-col w-full h-full overflow-hidden bg-n-background"
    >
      <div class="sticky top-0 z-10">
        <RecordHeader
          :title="document.title"
          :subtitle="typeLabel"
          :avatar-name="document.title"
          :status-label="currentStatusLabel"
          :status-class="currentStatusClass"
        >
          <template #navigation>
            <Breadcrumb :items="breadcrumbItems" @click="goToDocumentsList" />
          </template>
          <template #actions>
            <template v-if="nextStatuses.length">
              <ComboBox v-model="selectedStatus" :options="nextStatusOptions" class="w-44" />
              <Button
                :label="t('DOCUMENTS.MOVE')"
                size="sm"
                slate
                :is-loading="isTransitioning"
                :disabled="isTransitioning || !selectedStatus"
                @click="handleMove"
              />
            </template>
            <Button
              :label="t('DOCUMENTS.DETAIL.UPLOAD_VERSION')"
              icon="i-lucide-upload"
              size="sm"
              ghost
              slate
              :is-loading="isUploading"
              :disabled="isUploading"
              @click="chooseVersionFile"
            />
            <Button
              v-if="document.currentVersion"
              icon="i-lucide-download"
              size="sm"
              ghost
              slate
              :aria-label="t('DOCUMENTS.DOWNLOAD')"
              :is-loading="isDownloading"
              :disabled="isDownloading"
              @click="handleDownload()"
            />
            <Button
              v-if="canDelete"
              icon="i-lucide-trash-2"
              size="sm"
              ghost
              ruby
              :aria-label="t('DOCUMENTS.DETAIL.DELETE')"
              @click="deleteDialogRef?.open()"
            />
          </template>
        </RecordHeader>
      </div>

      <main class="flex-1 px-6 overflow-y-auto">
        <div class="flex flex-col w-full gap-6 py-6 mx-auto max-w-[48rem]">
          <!-- Quarantine banner -->
          <div v-if="isQuarantined" class="flex items-center gap-2">
            <span class="i-lucide-shield-alert size-4 shrink-0 text-n-ruby-11" />
            <span class="text-sm text-n-ruby-11">
              {{ t('DOCUMENTS.DETAIL.QUARANTINED_BANNER') }}
            </span>
          </div>

          <!-- Details -->
          <section class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.DETAILS') }}
            </h2>
            <div class="grid grid-cols-2 gap-4 p-4 rounded-lg bg-n-alpha-2">
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.TYPE') }}
                </span>
                <span class="text-sm text-n-slate-12">{{ typeLabel }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.PARTY') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ document.partyName || '-' }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.DUE_DATE') }}
                </span>
                <div class="flex items-center gap-2">
                  <span class="text-sm text-n-slate-12">{{ dueDateLabel }}</span>
                  <span
                    v-if="document.overdue"
                    class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-amber-9/10 text-n-amber-11"
                  >
                    {{ t('DOCUMENTS.OVERDUE') }}
                  </span>
                </div>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.OWNER') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ document.owner?.name || t('DOCUMENTS.UNASSIGNED') }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.VISIBILITY') }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium w-fit"
                  :class="visibilityChipClass"
                >
                  {{ visibilityLabel }}
                </span>
              </div>
              <div v-if="document.legalHold" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('DOCUMENTS.DETAIL.LEGAL_HOLD') }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-n-ruby-9/10 text-n-ruby-11"
                >
                  {{ t('DOCUMENTS.ACCESS.LEGAL_HOLD_ENABLED') }}
                </span>
              </div>
            </div>
          </section>

          <!-- Linked records -->
          <section v-if="linkedRecords.length" class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.LINKED_RECORDS') }}
            </h2>
            <div class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak">
              <div
                v-for="link in linkedRecords"
                :key="link.id"
                class="flex items-center gap-2 px-4 py-3"
              >
                <span class="i-lucide-link size-3.5 shrink-0 text-n-slate-8" />
                <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
                  {{ link.relatedName || t('DOCUMENTS.DETAIL.LINKED_RECORD_UNAVAILABLE') }}
                </span>
                <span
                  v-if="link.purpose"
                  class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-9/10 text-n-slate-11 shrink-0"
                >
                  {{ link.purpose }}
                </span>
              </div>
            </div>
          </section>

          <!-- Access -->
          <section class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.ACCESS.TITLE') }}
            </h2>
            <div class="flex flex-col gap-4 p-4 rounded-lg bg-n-alpha-2">
              <div class="grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3">
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('DOCUMENTS.DETAIL.VISIBILITY') }}
                  </span>
                  <span class="text-xs text-n-slate-11">{{ visibilityDescription }}</span>
                </div>
                <ComboBox
                  v-if="canManageAccess"
                  v-model="selectedVisibility"
                  :options="visibilityOptions"
                  @update:model-value="changeVisibility"
                />
                <span
                  v-else
                  class="justify-self-end px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="visibilityChipClass"
                >
                  {{ visibilityLabel }}
                </span>
              </div>

              <div class="grid grid-cols-[minmax(0,1fr)_11rem] items-center gap-3">
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('DOCUMENTS.ACCESS.LEGAL_HOLD') }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ t('DOCUMENTS.ACCESS.LEGAL_HOLD_DESCRIPTION') }}
                  </span>
                </div>
                <div class="flex justify-end">
                  <Button
                    v-if="isAdmin"
                    :label="
                      document.legalHold
                        ? t('DOCUMENTS.ACCESS.LEGAL_HOLD_DISABLE')
                        : t('DOCUMENTS.ACCESS.LEGAL_HOLD_ENABLE')
                    "
                    size="sm"
                    ghost
                    slate
                    :is-loading="isSavingAccess"
                    :disabled="isSavingAccess"
                    @click="legalHoldDialogRef?.open()"
                  />
                  <span
                    v-else-if="document.legalHold"
                    class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-ruby-9/10 text-n-ruby-11"
                  >
                    {{ t('DOCUMENTS.ACCESS.LEGAL_HOLD_ENABLED') }}
                  </span>
                </div>
              </div>

              <div
                v-if="grants.length"
                class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak"
              >
                <div
                  v-for="grant in grants"
                  :key="grant.id"
                  class="flex items-center gap-2 px-4 py-3 bg-n-background"
                >
                  <span
                    class="size-3.5 shrink-0 text-n-slate-8"
                    :class="grant.granteeType === 'Team' ? 'i-lucide-users' : 'i-lucide-user'"
                  />
                  <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
                    {{ grant.granteeName }}
                  </span>
                  <span
                    class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-9/10 text-n-slate-11 shrink-0"
                  >
                    {{ granteeTypeLabel(grant.granteeType) }}
                  </span>
                  <Button
                    v-if="canManageAccess"
                    icon="i-lucide-x"
                    size="sm"
                    ghost
                    slate
                    :aria-label="t('DOCUMENTS.ACCESS.REMOVE')"
                    :disabled="isSavingAccess"
                    @click="removeGrant(grant)"
                  />
                </div>
              </div>
              <p v-else class="text-sm text-n-slate-11">
                {{ t('DOCUMENTS.ACCESS.EMPTY') }}
              </p>

              <div v-if="canManageAccess" class="flex flex-wrap items-center gap-2">
                <ComboBox v-model="newGrantType" :options="grantTypeOptions" class="w-28" />
                <ComboBox
                  v-model="newGranteeId"
                  :options="granteeOptions"
                  :placeholder="granteePlaceholder"
                  class="flex-1 min-w-40"
                />
                <Button
                  :label="t('DOCUMENTS.ACCESS.ADD')"
                  size="sm"
                  slate
                  :is-loading="isSavingAccess"
                  :disabled="isSavingAccess || !newGranteeId"
                  @click="addGrant"
                />
              </div>
            </div>
          </section>

          <!-- Customer requests -->
          <section class="flex flex-col gap-3">
            <div class="flex items-center justify-between gap-3">
              <h2 class="text-sm font-medium text-n-slate-12">
                {{ t('DOCUMENTS.REQUESTS.TITLE') }}
              </h2>
              <Button
                :label="t('DOCUMENTS.REQUESTS.ADD')"
                icon="i-lucide-send"
                size="sm"
                slate
                @click="openRequestDialog"
              />
            </div>
            <div
              v-if="requests.length"
              class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak"
            >
              <div
                v-for="request in requests"
                :key="request.id"
                class="flex items-center gap-2 px-4 py-3"
              >
                <span class="i-lucide-file-up size-3.5 shrink-0 text-n-slate-8" />
                <div class="flex flex-col min-w-0 flex-1 gap-0.5">
                  <span class="text-sm truncate text-n-slate-12">
                    {{
                      request.recipientName ||
                      request.recipientEmail ||
                      t('DOCUMENTS.REQUESTS.RECIPIENT_UNKNOWN')
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      t('DOCUMENTS.REQUESTS.EXPIRES_AT', { date: formatDate(request.expiresAt) })
                    }}
                  </span>
                </div>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
                  :class="requestStateClass(request)"
                >
                  {{ requestStateLabel(request) }}
                </span>
                <Button
                  v-if="sessionLinks[request.id]"
                  icon="i-lucide-copy"
                  size="sm"
                  ghost
                  slate
                  :aria-label="t('DOCUMENTS.REQUESTS.COPY_LINK')"
                  @click="copySessionLink(request.id)"
                />
                <Button
                  v-if="requestState(request) === 'active' && canRevokeRequest(request)"
                  icon="i-lucide-ban"
                  size="sm"
                  ghost
                  ruby
                  :aria-label="t('DOCUMENTS.REQUESTS.REVOKE')"
                  @click="revokeRequest(request)"
                />
              </div>
            </div>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('DOCUMENTS.REQUESTS.EMPTY') }}
            </p>
          </section>

          <!-- Notes -->
          <section v-if="document.notes" class="flex flex-col gap-2">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.NOTES') }}
            </h2>
            <p class="text-sm text-n-slate-11 whitespace-pre-wrap">
              {{ document.notes }}
            </p>
          </section>

          <!-- Signatures -->
          <section class="flex flex-col gap-3">
            <div class="flex items-center justify-between gap-3">
              <h2 class="text-sm font-medium text-n-slate-12">
                {{ t('DOCUMENTS.SIGNATURES.TITLE') }}
              </h2>
              <div class="flex items-center gap-2">
                <Button
                  v-if="hasDocumentFile"
                  :label="t('DOCUMENTS.SIGNATURES.BUILDER.BUTTON')"
                  icon="i-lucide-layout-template"
                  size="sm"
                  ghost
                  slate
                  @click="openBuilderDialog"
                />
                <Button
                  v-if="canSendForSignature"
                  :label="t('DOCUMENTS.SIGNATURES.ADD')"
                  icon="i-lucide-pen-line"
                  size="sm"
                  slate
                  @click="openSignatureDialog"
                />
              </div>
            </div>
            <div
              v-if="signatureRequests.length"
              class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak"
            >
              <div
                v-for="request in signatureRequests"
                :key="request.id"
                class="flex items-center gap-2 px-4 py-3"
              >
                <span class="i-lucide-pen-line size-3.5 shrink-0 text-n-slate-8" />
                <div class="flex flex-col min-w-0 flex-1 gap-0.5">
                  <span class="text-sm truncate text-n-slate-12">
                    {{ request.signerName || request.signerEmail }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ signerMeta(request) }}
                  </span>
                </div>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
                  :class="signatureStatusClass(request.status)"
                >
                  {{ signatureStatusLabel(request.status) }}
                </span>
                <Button
                  v-if="!isTerminalSignature(request) && request.signingUrl"
                  icon="i-lucide-signature"
                  size="sm"
                  ghost
                  slate
                  :aria-label="t('DOCUMENTS.SIGNATURES.SIGN_NOW')"
                  @click="openSigningDialog(request)"
                />
                <Button
                  v-if="!isTerminalSignature(request)"
                  icon="i-lucide-ban"
                  size="sm"
                  ghost
                  ruby
                  :aria-label="t('DOCUMENTS.SIGNATURES.VOID')"
                  @click="confirmVoidSignatureRequest(request)"
                />
              </div>
            </div>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('DOCUMENTS.SIGNATURES.EMPTY') }}
            </p>
          </section>

          <!-- Versions -->
          <section class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.VERSIONS') }}
            </h2>
            <div
              v-if="versions.length"
              class="overflow-hidden rounded-lg border border-n-weak divide-y divide-n-weak"
            >
              <div
                v-for="version in versions"
                :key="version.id"
                class="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div class="flex flex-col min-w-0 gap-0.5">
                  <span class="flex items-center gap-2 min-w-0">
                    <span class="text-sm font-medium truncate text-n-slate-12">
                      {{ version.filename }}
                    </span>
                    <span class="text-xs text-n-slate-11 shrink-0">
                      {{ versionNumberLabel(version) }}
                    </span>
                    <span
                      v-if="version.id === document.currentVersion?.id"
                      class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-slate-9/10 text-n-slate-11 shrink-0"
                    >
                      {{ t('DOCUMENTS.DETAIL.CURRENT') }}
                    </span>
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ versionMeta(version) }}
                  </span>
                </div>
                <Button
                  icon="i-lucide-download"
                  size="sm"
                  ghost
                  slate
                  :aria-label="t('DOCUMENTS.DOWNLOAD')"
                  :is-loading="isDownloading"
                  :disabled="isDownloading"
                  @click="handleDownload(version.id, version.filename)"
                />
              </div>
            </div>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('DOCUMENTS.DETAIL.VERSIONS_EMPTY') }}
            </p>
          </section>

          <!-- History -->
          <section class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.HISTORY') }}
            </h2>
            <ol v-if="timelineEvents.length" class="flex flex-col gap-3">
              <li
                v-for="event in timelineEvents"
                :key="`${event.type}-${event.id}`"
                class="flex gap-3"
              >
                <span
                  v-if="event.type === 'access'"
                  class="i-lucide-download mt-1 size-3.5 shrink-0 text-n-slate-8"
                />
                <span v-else class="mt-1.5 size-2 shrink-0 rounded-full bg-n-slate-8" />
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-sm text-n-slate-12">
                    {{ event.type === 'access' ? accessEventLabel(event) : transitionLabel(event) }}
                  </span>
                  <span v-if="event.note" class="text-sm text-n-slate-11">
                    {{ event.note }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ historyMeta(event) }}
                  </span>
                </div>
              </li>
            </ol>
            <p v-else class="text-sm text-n-slate-11">
              {{ t('DOCUMENTS.DETAIL.HISTORY_EMPTY') }}
            </p>
          </section>

          <!-- Conversation activity (messages sent via the conversation channel) -->
          <section v-if="conversationMessages.length" class="flex flex-col gap-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ t('DOCUMENTS.DETAIL.CONVERSATION_ACTIVITY') }}
            </h2>
            <ol class="flex flex-col gap-3">
              <li v-for="message in conversationMessages" :key="message.id" class="flex gap-3">
                <span
                  v-if="message.messageType === 1"
                  class="i-lucide-download mt-1 size-3.5 shrink-0 text-n-slate-8"
                />
                <span v-else class="mt-1.5 size-2 shrink-0 rounded-full bg-n-slate-8" />
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-sm text-n-slate-12">
                    {{ message.content }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ message.sender?.name || t('CONVERSATION.SYSTEM') }} ·
                    {{ formatDate(message.createdAt) }}
                  </span>
                </div>
              </li>
            </ol>
          </section>

          <!-- Danger Zone - Delete -->
          <section
            v-if="canDelete"
            class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
          >
            <div class="flex flex-col gap-2">
              <h2 class="text-base font-medium text-n-slate-12">
                {{ t('DOCUMENTS.DETAIL.DELETE') }}
              </h2>
              <span class="text-sm text-n-slate-11">
                {{ t('DOCUMENTS.DETAIL.DELETE_DESCRIPTION') }}
              </span>
            </div>
            <Button
              :label="t('DOCUMENTS.DETAIL.DELETE')"
              color="ruby"
              :is-loading="isDeleting"
              :disabled="isDeleting"
              @click="deleteDialogRef?.open()"
            />
          </section>
        </div>
      </main>
    </section>

    <!-- Not Found State -->
    <div v-else class="flex flex-col items-center justify-center flex-1 h-full gap-4">
      <span class="i-lucide-file-text text-n-slate-9 size-12" />
      <p class="text-n-slate-11">{{ t('DOCUMENTS.DETAIL.NOT_FOUND') }}</p>
      <Button :label="t('DOCUMENTS.DETAIL.BACK')" @click="goToDocumentsList" />
    </div>

    <Dialog
      ref="archiveDialogRef"
      type="alert"
      :title="t('DOCUMENTS.CONFIRM_ARCHIVE.TITLE')"
      :description="t('DOCUMENTS.CONFIRM_ARCHIVE.DESCRIPTION')"
      :confirm-button-label="t('DOCUMENTS.CONFIRM_ARCHIVE.CONFIRM')"
      @confirm="runTransition('archived')"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('DOCUMENTS.DETAIL.DELETE_CONFIRM_TITLE')"
      :description="t('DOCUMENTS.DETAIL.DELETE_CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('DOCUMENTS.DETAIL.DELETE_CONFIRM')"
      @confirm="handleDelete"
    />
    <Dialog
      ref="legalHoldDialogRef"
      type="alert"
      :title="legalHoldDialogCopy.title"
      :description="legalHoldDialogCopy.description"
      :confirm-button-label="t('DOCUMENTS.ACCESS.LEGAL_HOLD_CONFIRM')"
      @confirm="toggleLegalHold"
    />
    <RequestDocumentDialog
      ref="requestDialogRef"
      :is-submitting="isCreatingRequest"
      @create="createRequest"
      @close="fetchRequests"
    />
    <SendForSignatureDialog
      ref="signatureDialogRef"
      :is-submitting="isCreatingSignatureRequest"
      @create="createSignatureRequest"
    />
    <DocusealSigningDialog ref="signingDialogRef" @completed="handleSigningComplete" />
    <DocusealBuilderDialog
      ref="builderDialogRef"
      :document-id="documentId"
      @saved="handleBuilderSaved"
    />
    <Dialog
      ref="voidDialogRef"
      type="alert"
      :title="t('DOCUMENTS.SIGNATURES.CONFIRM_VOID.TITLE')"
      :description="t('DOCUMENTS.SIGNATURES.CONFIRM_VOID.DESCRIPTION')"
      :confirm-button-label="t('DOCUMENTS.SIGNATURES.CONFIRM_VOID.CONFIRM')"
      @confirm="voidSignatureRequest"
    />
    <input
      ref="versionFileInput"
      type="file"
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      class="hidden"
      @change="uploadVersion"
    />
  </div>
</template>
